import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Edit, Trash2, Eye, MoreVertical } from "lucide-react";
import toast from "react-hot-toast";

const BASE = "https://api.livestockify.com/api/chennai";
const USERS_URL = `${BASE}/users`;
const PROJECTS_URL = `${BASE}/projects`;

const AllUser = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(null);

  const getToken = () => localStorage.getItem("token");

  /* ---------------- FETCH USERS + PROJECTS ---------------- */

  useEffect(() => {
    const load = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const [userRes, projectRes] = await Promise.all([
          axios.get(USERS_URL, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(PROJECTS_URL, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUsers(userRes.data || []);
        setProjects(projectRes.data || []);
      } catch {
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* ---------------- HELPERS ---------------- */

  const getUserProject = (user) =>
    projects.find(
      (p) => p.clientId === user.userUuid || p.clientId === user.clientId,
    );

  const toggleMenu = (id) => {
    setMenuOpen((prev) => (prev === id ? null : id));
  };

  const handleEditUser = (user) => {
    navigate("/chennai/admin/add-user", {
      state: { editData: user },
    });
  };

  const handleDeleteUser = async (user) => {
    try {
      await axios.delete(`${USERS_URL}/${user.userUuid}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("User deleted");
      setUsers((prev) => prev.filter((u) => u.userUuid !== user.userUuid));
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ---------------- SEARCH (NAME + DEVICE ID) ---------------- */

  const filteredUsers = users.filter((u) => {
    const nameMatch = `${u.userFirstname || ""} ${u.userLastname || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());

    const project = getUserProject(u);
    const deviceMatch = project?.devices?.some((d) =>
      d.deviceId?.toLowerCase().includes(search.toLowerCase()),
    );

    return nameMatch || deviceMatch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading users...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <button
          onClick={() => navigate("/chennai/admin/dashboard")}
          className="text-sm text-gray-600 hover:text-[#34C759]"
        >
          ← Go Back
        </button>

        <h2 className="text-2xl font-semibold">All Users</h2>

        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate("/chennai/admin/add-user")}
            className="
    bg-[#34C759] text-white
    px-5 py-2 rounded-md
    w-full sm:w-auto
    text-center
    font-medium
    shadow
    active:scale-95
  "
          >
            Add User
          </button>

          <div className="relative w-full sm:w-64">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full border rounded-md px-4 py-2"
            />
            <Search
              className="absolute right-3 top-2.5 text-gray-400"
              size={18}
            />
          </div>
        </div>
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden md:block border border-gray-300 rounded-md overflow-hidden">
        {/* TABLE HEADER */}
        <div className="grid grid-cols-7 bg-gray-100 text-sm font-semibold text-gray-700">
          <div className="px-4 py-3 border-r">First Name</div>
          <div className="px-4 py-3 border-r">Last Name</div>
          <div className="px-4 py-3 border-r">Project Name</div>
          <div className="px-4 py-3 border-r">Device ID</div>
          <div className="px-4 py-3 border-r text-center">Edit</div>
          <div className="px-4 py-3 border-r text-center">Delete</div>
          <div className="px-4 py-3 text-center">View</div>
        </div>

        {/* TABLE ROWS */}
        <div className="divide-y">
          {filteredUsers.map((u) => {
            const project = getUserProject(u);
            const deviceId = project?.devices?.[0]?.deviceId || "-";

            return (
              <div
                key={u.userUuid}
                className="grid grid-cols-7 items-center bg-white"
              >
                <div className="px-4 py-3 border-r">
                  {u.userFirstname || "-"}
                </div>
                <div className="px-4 py-3 border-r">
                  {u.userLastname || "-"}
                </div>
                <div className="px-4 py-3 border-r">
                  {project?.projectName || "-"}
                </div>
                <div className="px-4 py-3 border-r text-sm break-all">
                  {deviceId}
                </div>

                <div className="px-4 py-3 border-r flex justify-center">
                  <button
                    onClick={() => handleEditUser(u)}
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Edit size={16} /> Edit
                  </button>
                </div>

                <div className="px-4 py-3 border-r flex justify-center">
                  <button
                    onClick={() => handleDeleteUser(u)}
                    className="text-red-500 hover:underline flex items-center gap-1"
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>

                <div className="px-4 py-3 flex justify-center">
                  <button
                    onClick={() =>
                      project?.projectUuid &&
                      navigate(`/chennai/admin/metrics/${project.projectUuid}`)
                    }
                    className="text-[#34C759] hover:underline flex items-center gap-1"
                  >
                    <Eye size={16} /> View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================= MOBILE / TABLET CARDS ================= */}
      <div className="md:hidden space-y-4">
        {filteredUsers.map((u) => {
          const project = getUserProject(u);
          const deviceId = project?.devices?.[0]?.deviceId || "-";

          return (
            <div
              key={u.userUuid}
              className="bg-white border rounded-xl p-4 shadow-sm relative"
            >
              {/* TOP */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">
                    {u.userFirstname} {u.userLastname}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {project?.projectName || "-"}
                  </p>
                </div>

                <button onClick={() => toggleMenu(u.userUuid)}>
                  <MoreVertical />
                </button>

                {menuOpen === u.userUuid && (
                  <div className="absolute right-4 top-12 bg-white border rounded shadow w-32 z-50">
                    <button
                      onClick={() => handleEditUser(u)}
                      className="flex items-center gap-2 px-3 py-2 w-full"
                    >
                      <Edit size={16} /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u)}
                      className="flex items-center gap-2 px-3 py-2 text-red-500 w-full"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                )}
              </div>

              {/* BOTTOM ROW */}
              <div className="mt-4 flex items-center justify-between">
                {/* DEVICE ID - LEFT */}
                <p className="text-xs text-gray-500 break-all">
                  Device: {deviceId}
                </p>

                {/* VIEW - RIGHT */}
                <button
                  onClick={() =>
                    project?.projectUuid &&
                    navigate(`/chennai/admin/metrics/${project.projectUuid}`)
                  }
                  className="text-[#34C759] flex items-center gap-1"
                >
                  <Eye size={16} /> View
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllUser;
